import styled, { css } from 'styled-components'

import { orange1000, orange500 } from 'src/styles/color-theme'

export type Variants = `default` | `danger` | `warning`

type StyledModalComponentProps = {
  $variant?: Variants
  $isFullScreen?: boolean
  $isWithFooter?: boolean
  $styles?: string
}

const bgMap: Record<Variants, string> = {
  default: `#111d2c`,
  danger: `#FF4D4F`,
  warning: orange500,
}
const fontColorMap: Record<Variants, string> = {
  default: `white`,
  danger: `white`,
  warning: orange1000,
}

/**
 * The house Modal's variant styling, written for BOTH renderings of the
 * `src/antd/Modal` seam.
 *
 * With the design toggle off the seam renders antd, so the `.ant-modal-*`
 * rules below are the live ones and are unchanged. With it on the seam renders
 * the shadcn Dialog, whose DOM is `data-slot` attributes and carries no
 * `.ant-*` class at all — so each rule has a `[data-slot='…']` twin beside it.
 * This is the same shape the Tag port used for `.ant-tag`: keep both, so
 * nothing about the antd path moves.
 *
 * FULLSCREEN IS ONE RESULT REACHED BY TWO MECHANISMS, and saying so is the
 * point of this paragraph. Both renderings end up the same: the panel fills
 * the viewport with NO padding and no border, the header band sits flush at
 * the top, and the body takes the rest. HOW the body gets its height is where
 * they part, and there is no shared set of numbers to maintain.
 *
 * antd is told in inline styles on its top-anchored, statically positioned
 * `.ant-modal` root, ending in `padding: 0` (see `./index.tsx`), and its body
 * height is the calibrated `calc(100vh - 100px)` with a footer /
 * `calc(100vh - 55px)` without. Those constants are LIVE — measured at 845px —
 * and nothing here touches that path.
 *
 * The shadcn panel is `fixed`, centred, padded `p-6` and clamped by a
 * max-height, none of which an inline style can undo, so
 * `&[data-slot='dialog-content']` carries its fullscreen instead. The `&`
 * matters: `styled(Modal)` puts the class ON that element, not on an ancestor.
 * Its body deliberately carries NO height: the seam renders that div as
 * `min-h-0 flex-1` inside a `flex flex-col` panel, so `flex: 1 1 0%` supplies
 * the used main size (measured 809.469px) and any `height` here would be dead
 * rule a maintainer would wrongly trust. Only `overflow: hidden` is kept,
 * because that one does apply.
 *
 * The `[data-slot='modal-header']` band needs negative margins the antd one
 * does not: antd's header is a full-bleed child of `.ant-modal-content`, while
 * the Dialog panel is padded, so the band has to bleed back out to the panel
 * edges to span the modal width. Fullscreen removes that padding, so the same
 * band has to drop the bleed with it or it would hang 24px off the top and
 * left edges.
 */
const StyledModal = css<StyledModalComponentProps>`
  .ant-modal-header {
    background: ${({ $variant = `default` }) => bgMap[$variant]} !important;

    .ant-modal-title {
      color: ${({ $variant = `default` }) => fontColorMap[$variant]} !important;
    }
  }

  .ant-modal-close {
    .ant-modal-close-icon {
      color: ${({ $variant = `default` }) => fontColorMap[$variant]} !important;
    }
  }

  .ant-modal-body {
    ${({ $isFullScreen, $isWithFooter }) =>
      $isFullScreen
        ? `
      height: calc(100vh - ${$isWithFooter ? `100` : `55`}px);
      overflow: hidden;
    `
        : ``}
  }

  [data-slot='modal-header'] {
    background: ${({ $variant = `default` }) => bgMap[$variant]};
    margin: -1.5rem -1.5rem 1rem;
    padding: 12px 24px;
    border-bottom: none;
    border-radius: 8px 8px 0 0;

    ${({ $isFullScreen }) =>
      $isFullScreen
        ? `
      margin: 0 0 1rem;
      border-radius: 0;
    `
        : ``}

    [data-slot='modal-title'] {
      color: ${({ $variant = `default` }) => fontColorMap[$variant]};
    }
  }

  [data-slot='dialog-close'] {
    color: ${({ $variant = `default` }) => fontColorMap[$variant]};
  }

  &[data-slot='dialog-content'] {
    ${({ $isFullScreen }) =>
      $isFullScreen
        ? `
      top: 0;
      left: 0;
      /*
       * BOTH resets are required, and neither may be deleted. Tailwind v4
       * emits the panel's centring utilities (\`translate-x-[-50%]\`,
       * \`translate-y-[-50%]\`) as the standalone CSS \`translate\` property, so
       * resetting only \`transform\` is inert and leaves the panel half
       * off-screen up and left. \`transform: none\` stays beside it because it
       * costs nothing and survives shadcn or Tailwind moving the centring
       * back onto \`transform\`.
       */
      translate: none;
      transform: none;
      width: 100vw;
      max-width: 100vw;
      height: 100vh;
      max-height: 100vh;
      padding: 0;
      border: 0;
      border-radius: 0;
    `
        : ``}
  }

  [data-slot='modal-body'] {
    ${({ $isFullScreen }) =>
      $isFullScreen
        ? `
      overflow: hidden;
    `
        : ``}
  }
  ${({ $styles = `` }) => $styles}
`

export const ModalLoadContainer = styled.div`
  width: 100%;
  left: 50%;
  top: 50%;

  .ant-modal-body {
    height: 250px !important;
  }
`

export default StyledModal
